import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { getNegotiationList } from "../../../../services/AppService";
import { useTranslation } from "react-i18next";

const AddAgreementForm = ({ onSubmit, editData, onClose, open }) => {
    const {t}=useTranslation();

   const [formData, setFormData] = useState({
        tenderProcessId: "",
        negotiationId: "",
        agreementNo: "",
        agreementDate: "",
        bpg: "",
        bpgDate: ""
    });

    const [displayFields, setDisplayFields] = useState({
        workTitle: "",
        tenderDate: "",
        tenderValue: "",
        bidderName: "",
        aboveBelow: "",
        bidAmount: ""
    });

    const [negotiationList, setNegotiationList] = useState([]);

    // 1. Fetch negotiations on open
    useEffect(() => {
        if (open) {
            fetchNegotiations();
        }
    }, [open]);

    const fetchNegotiations = async () => {
        try {
            const res = await getNegotiationList("", 0, 1000);
            setNegotiationList(res.content || []);
        } catch (err) {
            console.error("Failed to fetch negotiations", err);
        }
    };

    // 2. Handle Initial Load (Edit vs Add)
    useEffect(() => {
        if (editData && open) {
            // Map IDs for the form inputs
            setFormData({
                tenderProcessId: editData.tenderProcessId || "",
                negotiationId: editData.negotiationId || "",
                agreementNo: editData.agreementNo || "",
                agreementDate: editData.agreementDate || "",
                bpg: editData.bpg || "",
                bpgDate: editData.bpgDate || ""
            });

            // ✅ PRE-FILL LABELS DIRECTLY FROM editData
            // This ensures data shows up instantly without waiting for negotiationList to load
            setDisplayFields({
                workTitle: editData.workTitle || "",
                tenderDate: editData.tenderDate || "",
                tenderValue: editData.tenderValue || "",
                bidderName: editData.bidderName || "",
                aboveBelow: editData.aboveBelow || "",
                bidAmount: editData.bidAmount || ""
            });
        } else if (open) {
            resetForm();
        }
    }, [editData, open]);

    // 3. Auto-fill logic when user manually changes selection
    const handleNegotiationChange = (e) => {
        const negId = e.target.value;
        setFormData(prev => ({ ...prev, negotiationId: negId }));

        if (negId) {
            const selected = negotiationList.find(n => n.id === Number(negId));
            if (selected) {
                setDisplayFields({
                    workTitle: selected.workTitle || "",
                    tenderDate: selected.tenderDate || "",
                    tenderValue: selected.tenderValue || "",
                    bidderName: selected.bidderName || "",
                    aboveBelow: selected.aboveBelow || "",
                    bidAmount: selected.bidAmount || ""
                });
                setFormData(prev => ({ 
                    ...prev, 
                    negotiationId: negId,
                    tenderProcessId: selected.tenderProcessId 
                }));
            }
        } else {
            setDisplayFields({
                workTitle: "", tenderDate: "", tenderValue: "",
                bidderName: "", aboveBelow: "", bidAmount: ""
            });
        }
    };

    const resetForm = () => {
        setFormData({
            tenderProcessId: "", negotiationId: "", agreementNo: "",
            agreementDate: "", bpg: "", bpgDate: ""
        });
        setDisplayFields({
            workTitle: "", tenderDate: "", tenderValue: "",
            bidderName: "", aboveBelow: "", bidAmount: ""
        });
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            tenderProcessId: Number(formData.tenderProcessId),
            negotiationId: Number(formData.negotiationId),
        };
        onSubmit(payload);
    };

    if (!open) return null;


    return (
      <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1100px', maxWidth: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">{t("pageTitles.agreementProcessList")}</div>
                    <button className="admin-modal-close" onClick={onClose}><IoClose /></button>
                </div>
                <div className="staff-form-body">
                    <form onSubmit={handleSubmit}>

                        {/* Section 1: Search Tender (Auto-filled up to Bid Amount) */}
                        <fieldset className="border p-4 rounded-md mb-4">
                            <legend className="text-sm font-bold px-2">{t("forms.agreementProcess.searchTender")}:</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("tables.agreementProcess.tenderId")} <span>*</span></label>
                                    <select
                                        name="negotiationId"
                                        value={formData.negotiationId}
                                        onChange={handleNegotiationChange}
                                        className="staff-modal-input"
                                        required
                                    >
                                        <option value="">Select Tender Id</option>
                                        {negotiationList.map(n => (
                                            <option key={n.id} value={n.id}>{n.tenderId}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.tenderDate")}</label>
                                    <input value={displayFields.tenderDate} className="staff-modal-input bg-gray-100" placeholder="Tender Date" readOnly />
                                </div>
                                <div className="form-group md:col-span-2">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.workTitle")}</label>
                                    <input value={displayFields.workTitle} className="staff-modal-input bg-gray-100" placeholder="Work Title" readOnly />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.tenderValue")}</label>
                                    <input value={displayFields.tenderValue} className="staff-modal-input bg-gray-100" placeholder="Tender Value" readOnly />
                                </div>
                            </div>
                        </fieldset>

                        {/* Section 2: Contractor Details (Read-only from Negotiation) */}
                        <fieldset className="border p-4 rounded-md mb-4">
                            <legend className="text-sm font-bold px-2">{t("forms.agreementProcess.contractor")}:</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-group md:col-span-2">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.bidderName")}</label>
                                    <input value={displayFields.bidderName} className="staff-modal-input bg-gray-100" placeholder="Bidder Name" readOnly />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.aboveBelow")}</label>
                                    <input value={displayFields.aboveBelow ? `${displayFields.aboveBelow}%` : ""} className="staff-modal-input bg-gray-100" placeholder="Above / Below %" readOnly />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.bidAmount")}</label>
                                    <input value={displayFields.bidAmount} className="staff-modal-input bg-gray-100" placeholder="Bid Amount" readOnly />
                                </div>
                            </div>
                        </fieldset>

                        {/* Section 3: Agreement Details (Editable) */}
                        <fieldset className="border p-4 rounded-md mb-4 border-blue-200 bg-blue-50/10">
                            <legend className="text-sm font-bold px-2">{t("forms.agreementProcess.agreementAndBpgInfo")}:</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.agreementNo")} <span>*</span></label>
                                    <input
                                        name="agreementNo"
                                        placeholder="Enter Agreement Number"
                                        value={formData.agreementNo}
                                        onChange={handleFieldChange}
                                        className="staff-modal-input border-blue-300"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.agreementDate")} <span>*</span></label>
                                    <input
                                        type="date"
                                        name="agreementDate"
                                        value={formData.agreementDate}
                                        onChange={handleFieldChange}
                                        className="staff-modal-input border-blue-300"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.bpg")} <span>*</span></label>
                                    <input
                                        name="bpg"
                                        placeholder="Enter BPG"
                                        value={formData.bpg}
                                        onChange={handleFieldChange}
                                        className="staff-modal-input border-blue-300"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="staff-modal-label">{t("forms.agreementProcess.bpgDate")} <span>*</span></label>
                                    <input
                                        type="date"
                                        name="bpgDate"
                                        value={formData.bpgDate}
                                        onChange={handleFieldChange}
                                        className="staff-modal-input border-blue-300"
                                        required
                                    />
                                </div>
                            </div>
                        </fieldset>

                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-12 uppercase font-bold">{t("common.save")}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddAgreementForm;